'use client'

import { CopyButton } from '../ui/copy-button'
import { formatPrice } from '~/utils/format'
import { memo } from 'react'

import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
interface BankTransferInfoProps {
  bankName: string
  bankAccount: string
  bankAccountHolder: string
  amount: number
  bookingNo: string
  customerName: string
}

interface InfoRowProps {
  label: string
  value: string
}

const InfoRow = memo(function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="grid">
      <span className="text-[12px] text-[#666B74] sm:text-[14px]">{label}</span>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium leading-[16px] text-[#2D2E32] sm:text-[14px]">
          {value}
        </span>
        <CopyButton text={value} fieldId={label} />
      </div>
    </div>
  )
})

const removeDiacritics = (str: string) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
}

export const BankTransferInfo = memo(function BankTransferInfo({
  bankName,
  bankAccount,
  bankAccountHolder,
  amount,
  bookingNo,
  customerName,
}: BankTransferInfoProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const transferContent = `MHG ${bookingNo} ${removeDiacritics(customerName).toUpperCase()}`

  const transferInfo = [
    { label: t('booking.bankName'), value: bankName },
    { label: t('booking.bankAccount'), value: bankAccount },
    { label: t('booking.accountHolder'), value: bankAccountHolder },
    { label: t('booking.amount'), value: formatPrice(amount) },
    { label: t('booking.content'), value: transferContent },
  ]

  return (
    <div className="mt-4 rounded-lg border-b-gray-500 bg-gray-100">
      <div className="xs:max-w-md mx-auto grid gap-3 p-4">
        {transferInfo.map((info) => (
          <InfoRow key={info.label} label={info.label} value={info.value} />
        ))}
      </div>
    </div>
  )
})

function arePropsEqual(prevProps: BankTransferInfoProps, nextProps: BankTransferInfoProps) {
  return (
    prevProps.bankName === nextProps.bankName &&
    prevProps.bankAccount === nextProps.bankAccount &&
    prevProps.bankAccountHolder === nextProps.bankAccountHolder &&
    prevProps.amount === nextProps.amount &&
    prevProps.bookingNo === nextProps.bookingNo &&
    prevProps.customerName === nextProps.customerName
  )
}

export default memo(BankTransferInfo, arePropsEqual)
